// Pure projections that turn the planner's existing inputs (plan JSON, requirement trees,
// DegreeRequirementService progress, catalog prerequisites) into the slot-based planner model.
// Nothing here recalculates degree progress: requirement counts come from the progress
// service output, and this module only arranges them for display.

import type { RutgersCourse, Semester, SemesterItem } from '@/app/types/interfaces';
import { normalize, isLikelyCourseCode } from '@/app/services/degreeRequirementService';
import { parsePrerequisiteGroups } from '@/app/services/prerequisiteChecker';
import { getCatalogSeasonFlags } from '@/app/lib/classesCatalogSeasons';
import { equivalentCourseCodes } from '@/app/lib/courseEquivalencies';

export type SlotState = 'earned' | 'planned' | 'open';

export interface Slot {
  state: SlotState;
  code?: string;
  semesterNumber?: number;
  termName?: string;
  /** Planned through a course-list placeholder rather than an exact course. */
  listName?: string;
}

export interface PlannerRequirement {
  key: string;
  id: string;
  title: string;
  description?: string;
  degreeId: number;
  degreeName: string;
  programIndex: number;
  sectionTitle: string;
  /** Exact Rutgers course codes (normalized) a student can use to fill this requirement. */
  pool: string[];
  /** Named course lists that can be held as a placeholder in a term. */
  listNames: string[];
  mode: 'courses' | 'credits';
  slots: Slot[];
  creditsNeeded: number;
  creditsEarned: number;
  creditsPlanned: number;
  openCount: number;
  evaluated: boolean;
}

export interface PlannerSection {
  key: string;
  title: string;
  logic: 'and' | 'or';
  requirements: PlannerRequirement[];
}

export interface PlannerProgram {
  degreeId: number;
  degreeName: string;
  programIndex: number;
  captured: boolean;
  sections: PlannerSection[];
  requirements: PlannerRequirement[];
  slotTotals: { needed: number; earned: number; planned: number; open: number };
}

export interface PlannerTerm {
  number: number;
  name: string;
  season: string | null;
  items: Array<{ item: SemesterItem; index: number }>;
  credits: number;
}

export type ClauseStatus = 'earned' | 'planned' | 'late' | 'missing' | 'unknown';

export interface PrereqClause {
  options: string[];
  status: ClauseStatus;
  satisfiedBy?: string;
  termName?: string;
}

/** Program accents are mid-tone so they read on both light and dark surfaces. */
export const PROGRAM_COLORS = ['#3b82f6', '#0d9488', '#d97706', '#db2777', '#65a30d', '#0891b2'];
export const programColor = (index: number) => PROGRAM_COLORS[index % PROGRAM_COLORS.length];

export const formatCode = (code: string): string => {
  const digits = String(code).replace(/[^0-9]/g, '');
  return digits.length >= 8 ? `${digits.slice(0, 2)}:${digits.slice(2, 5)}:${digits.slice(5)}` : code;
};

/** Department and number, e.g. "198:344", for tight spaces. */
export const shortCode = (code: string): string => {
  const prefix = String(code).startsWith('≥') ? '≥ ' : '';
  const digits = String(code).replace(/[^0-9]/g, '');
  if (digits.length < 8) return code;
  // Only School of Arts and Sciences (01) codes drop their school prefix; others keep it so they stay unambiguous.
  return digits.startsWith('01') ? `${prefix}${digits.slice(2, 5)}:${digits.slice(5)}` : `${prefix}${formatCode(digits)}`;
};

const SMALL_WORDS = new Set(['a', 'an', 'and', 'as', 'at', 'by', 'for', 'in', 'of', 'on', 'or', 'the', 'to', 'with']);
const ROMAN = /^(i|ii|iii|iv|v|vi)$/i;

/** Catalog titles arrive in capitals ("INTRO LINEAR ALGEBRA"); show them in title case. */
export function displayTitle(raw: string): string {
  // Catalog titles can carry fixed-width padding; it leaks into accessible names if kept.
  const title = (raw || '').trim();
  if (!title || /[a-z]/.test(title)) return title;
  return title
    .toLowerCase()
    .split(/(\s+|-|\/)/)
    .map((word, i) => {
      if (!word.trim() || word === '-' || word === '/') return word;
      if (ROMAN.test(word)) return word.toUpperCase();
      if (i > 0 && SMALL_WORDS.has(word)) return word;
      return word[0].toUpperCase() + word.slice(1);
    })
    .join('')
    .replace(/(\d)([a-z])/g, (_, d, l) => `${d}${l.toUpperCase()}`);
}

export const seasonOf =(termName: string): string | null => {
  const match = /(Fall|Spring|Summer|Winter)/i.exec(termName || '');
  return match ? match[1][0].toUpperCase() + match[1].slice(1).toLowerCase() : null;
};

export function offeredSeasons(code: string): string[] | null {
  const flags = getCatalogSeasonFlags(normalize(code));
  if (!flags) return null;
  const seasons = [
    flags.offeredFall ? 'Fall' : null,
    flags.offeredSpring ? 'Spring' : null,
    flags.offeredSummer ? 'Summer' : null,
    flags.offeredWinter ? 'Winter' : null,
  ].filter((value): value is string => Boolean(value));
  return seasons.length ? seasons : null;
}

export function itemCode(item: SemesterItem): string | null {
  if (item.kind === 'course_list') return item.selectedCourseCode ? normalize(item.selectedCourseCode) : null;
  return normalize(String((item as { code?: string }).code || '')) || null;
}

export function buildTerms(semesters: Semester[], creditsMap: Map<string, number>, listCredits: (name: string) => number): PlannerTerm[] {
  return [...semesters]
    .sort((a, b) => a.number - b.number)
    .map((semester) => {
      const items = (semester.courses || []).map((item, index) => ({ item, index }));
      const credits = items.reduce((sum, { item }) => {
        if (item.kind === 'course_list') return sum + (item.credits || listCredits(item.mappingName)) * (item.quantity || 1);
        return sum + (Number(item.credits) || creditsMap.get(normalize(item.code)) || 0);
      }, 0);
      return { number: semester.number, name: semester.name, season: seasonOf(semester.name), items, credits: Math.round(credits * 10) / 10 };
    });
}

/** Where each planned course sits, keyed by normalized code. */
export function buildPlacement(terms: PlannerTerm[]): Map<string, { semesterNumber: number; termName: string; order: number; index: number }> {
  const map = new Map<string, { semesterNumber: number; termName: string; order: number; index: number }>();
  terms.forEach((term, order) => {
    term.items.forEach(({ item, index }) => {
      const code = itemCode(item);
      if (code && !map.has(code)) map.set(code, { semesterNumber: term.number, termName: term.name, order, index });
    });
  });
  return map;
}

function equivalentsOf(code: string): string[] {
  const norm = normalize(code);
  return [norm, ...equivalentCourseCodes(norm)];
}

interface BuildProgramsInput {
  selectedDegreeRequirements: Array<{ degreeId: number; degreeName: string; requirements?: unknown[]; coverageUnavailable?: boolean }>;
  degreeProgress: Map<number, { requirementsProgress?: Array<Record<string, unknown>> }> | null;
  expandedPools: Map<string, string[]>;
  completed: Set<string>;
  placement: ReturnType<typeof buildPlacement>;
  terms: PlannerTerm[];
}

type RawRequirement = {
  id?: string;
  title?: string;
  description?: string;
  logic?: 'and' | 'or';
  courses_needed?: number;
  credits_needed?: number | null;
  course_pool?: string[];
  requirements?: RawRequirement[];
};

export function buildPrograms({ selectedDegreeRequirements, degreeProgress, expandedPools, completed, placement, terms }: BuildProgramsInput): PlannerProgram[] {
  const listPlacements = new Map<string, Array<{ semesterNumber: number; termName: string }>>();
  terms.forEach((term) => term.items.forEach(({ item }) => {
    if (item.kind !== 'course_list') return;
    const list = listPlacements.get(item.mappingName) || [];
    for (let i = 0; i < (item.quantity || 1); i++) list.push({ semesterNumber: term.number, termName: term.name });
    listPlacements.set(item.mappingName, list);
  }));

  return selectedDegreeRequirements.map((degree, programIndex) => {
    const progressList = degreeProgress?.get(degree.degreeId)?.requirementsProgress || [];
    const requirements: PlannerRequirement[] = [];
    const sections: PlannerSection[] = [];
    const usedListSlots = new Map<string, number>();

    const toLeaf = (req: RawRequirement, sectionTitle: string, path: string): PlannerRequirement => {
      const pool: string[] = [];
      const listNames: string[] = [];
      (req.course_pool || []).forEach((entry) => {
        if (isLikelyCourseCode(entry)) pool.push(normalize(entry));
        else {
          listNames.push(entry);
          (expandedPools.get(entry) || []).forEach((code) => pool.push(normalize(code)));
        }
      });
      const uniquePool = Array.from(new Set(pool));
      const progress = progressList.find((entry) => entry.requirementId === req.id) as
        | { completedCourses?: string[]; plannedCourses?: string[]; neededCourses?: number; neededCredits?: number; creditsCompleted?: number; creditsPlanned?: number }
        | undefined;

      const neededCourses = Number(req.courses_needed || progress?.neededCourses || 0);
      const neededCredits = Number(req.credits_needed || progress?.neededCredits || 0);
      const mode: 'courses' | 'credits' = neededCourses > 0 || !neededCredits ? 'courses' : 'credits';
      const slotCount = mode === 'courses' ? Math.max(1, neededCourses || 1) : 0;

      // Earned and planned evidence: prefer the progress service, fall back to pool matching.
      const poolSet = new Set(uniquePool.flatMap(equivalentsOf));
      const earnedCodes = progress?.completedCourses
        ? progress.completedCourses.map(normalize)
        : uniquePool.filter((code) => equivalentsOf(code).some((c) => completed.has(c)));
      const plannedRaw = progress?.plannedCourses
        ? progress.plannedCourses
        : uniquePool.filter((code) => placement.has(code));
      const plannedCodes = plannedRaw.filter((code) => !String(code).startsWith('__planned_list_block__')).map(normalize);
      const plannedLists = plannedRaw.filter((code) => String(code).startsWith('__planned_list_block__')).map((code) => String(code).replace('__planned_list_block__', ''));

      const slots: Slot[] = [];
      if (mode === 'courses') {
        earnedCodes.forEach((code) => { if (slots.length < slotCount) slots.push({ state: 'earned', code }); });
        plannedCodes.forEach((code) => {
          if (slots.length >= slotCount) return;
          const where = placement.get(code) || Array.from(poolSet).map((c) => placement.get(c)).find(Boolean);
          slots.push({ state: 'planned', code, semesterNumber: where?.semesterNumber, termName: where?.termName });
        });
        Array.from(new Set(plannedLists.length ? plannedLists : listNames)).forEach((name) => {
          const placed = listPlacements.get(name) || [];
          let used = usedListSlots.get(name) || 0;
          while (slots.length < slotCount && used < placed.length && (plannedLists.length || listNames.includes(name))) {
            slots.push({ state: 'planned', listName: name, semesterNumber: placed[used].semesterNumber, termName: placed[used].termName });
            used += 1;
          }
          usedListSlots.set(name, used);
        });
        while (slots.length < slotCount) slots.push({ state: 'open' });
      }

      const creditsEarned = Number(progress?.creditsCompleted || 0);
      const creditsPlanned = Number(progress?.creditsPlanned || 0);
      const openCount = mode === 'courses'
        ? slots.filter((slot) => slot.state === 'open').length
        : (creditsEarned + creditsPlanned >= neededCredits ? 0 : 1);

      return {
        key: `${degree.degreeId}:${path}`,
        id: req.id || path,
        title: req.title || 'Requirement',
        description: req.description,
        degreeId: degree.degreeId,
        degreeName: degree.degreeName,
        programIndex,
        sectionTitle,
        pool: uniquePool,
        listNames,
        mode,
        slots,
        creditsNeeded: neededCredits,
        creditsEarned,
        creditsPlanned,
        openCount,
        evaluated: Boolean(progress),
      };
    };

    const collect = (reqs: RawRequirement[], sectionTitle: string, path: string, out: PlannerRequirement[]) => {
      reqs.forEach((req, i) => {
        const nextPath = `${path}/${req.id || i}`;
        if (Array.isArray(req.course_pool) && req.course_pool.length) out.push(toLeaf(req, sectionTitle, nextPath));
        if (Array.isArray(req.requirements) && req.requirements.length) collect(req.requirements, sectionTitle, nextPath, out);
      });
    };

    ((degree.requirements || []) as RawRequirement[]).forEach((top, i) => {
      const leaves: PlannerRequirement[] = [];
      const title = top.title || `Requirement ${i + 1}`;
      collect([top], title, String(i), leaves);
      if (!leaves.length) return;
      sections.push({ key: `${degree.degreeId}:${i}`, title, logic: top.logic === 'or' ? 'or' : 'and', requirements: leaves });
      requirements.push(...leaves);
    });

    const slotTotals = requirements.reduce((totals, req) => {
      if (req.mode === 'credits') {
        totals.needed += 1;
        if (req.openCount === 0) { if (req.creditsEarned >= req.creditsNeeded) totals.earned += 1; else totals.planned += 1; } else totals.open += 1;
        return totals;
      }
      totals.needed += req.slots.length;
      req.slots.forEach((slot) => { totals[slot.state === 'open' ? 'open' : slot.state] += 1; });
      return totals;
    }, { needed: 0, earned: 0, planned: 0, open: 0 });

    return {
      degreeId: degree.degreeId,
      degreeName: degree.degreeName,
      programIndex,
      captured: !degree.coverageUnavailable && requirements.length > 0,
      sections,
      requirements,
      slotTotals,
    };
  });
}

/** Which requirements a course can count toward, across every program in the plan. */
export function requirementsForCourse(code: string, programs: PlannerProgram[]): PlannerRequirement[] {
  const ids = equivalentsOf(code);
  return programs.flatMap((program) => program.requirements.filter((req) => req.pool.some((entry) => ids.includes(entry))));
}

export interface CatalogIndex {
  byCode: Map<string, RutgersCourse>;
  prereqGroups: Map<string, string[][][]>;
  dependents: Map<string, string[]>;
}

export function buildCatalogIndex(courses: RutgersCourse[]): CatalogIndex {
  const byCode = new Map<string, RutgersCourse>();
  const prereqGroups = new Map<string, string[][][]>();
  const dependents = new Map<string, string[]>();
  courses.forEach((course) => {
    const code = normalize(course.baseId || course.id || '');
    if (!code || byCode.has(code)) return;
    byCode.set(code, course);
    if (course.prerequisites) {
      const groups = parsePrerequisiteGroups(course.prerequisites);
      if (groups.length) {
        prereqGroups.set(code, groups);
        const mentioned = new Set(groups.flat(2).filter((entry) => !entry.startsWith('>=')).map(normalize));
        mentioned.forEach((pre) => {
          const list = dependents.get(pre) || [];
          list.push(code);
          dependents.set(pre, list);
        });
      }
    }
  });
  return { byCode, prereqGroups, dependents };
}

/**
 * Prerequisite clauses for a course placed at `order` (index into the sorted terms, or
 * Infinity for "not placed"). Picks the OR-group that is closest to satisfied.
 */
export function prerequisiteClauses(
  code: string,
  order: number,
  index: CatalogIndex,
  completed: Set<string>,
  placement: ReturnType<typeof buildPlacement>,
): PrereqClause[] {
  const groups = index.prereqGroups.get(normalize(code));
  if (!groups?.length) return [];
  const evaluateClause = (options: string[]): PrereqClause => {
    if (options.some((opt) => opt.startsWith('>='))) {
      // "Any course equal or greater than X": a same-subject course numbered at or above X.
      const floors = options.map((opt) => normalize(opt.replace(/^>=/, '')));
      const meets = (code: string) => floors.some((floor) => code.slice(0, 5) === floor.slice(0, 5) && Number(code.slice(5)) >= Number(floor.slice(5)));
      const label = floors.map((floor) => `≥ ${floor}`);
      const earned = Array.from(completed).find(meets);
      if (earned) return { options: label, status: 'earned', satisfiedBy: earned };
      const planned = Array.from(placement.entries()).filter(([code, where]) => meets(code) && where.order < order).sort((a, b) => a[1].order - b[1].order)[0];
      if (planned) return { options: label, status: 'planned', satisfiedBy: planned[0], termName: planned[1].termName };
      return { options: label, status: 'missing' };
    }
    for (const opt of options) {
      if (equivalentsOf(opt).some((c) => completed.has(c))) return { options, status: 'earned', satisfiedBy: normalize(opt) };
    }
    let late: PrereqClause | null = null;
    for (const opt of options) {
      const where = equivalentsOf(opt).map((c) => placement.get(c)).find(Boolean);
      if (where && where.order < order) return { options, status: 'planned', satisfiedBy: normalize(opt), termName: where.termName };
      if (where && !late) late = { options, status: 'late', satisfiedBy: normalize(opt), termName: where.termName };
    }
    return late || { options, status: 'missing' };
  };
  const scored = groups.map((group) => {
    const clauses = group.map(evaluateClause);
    const unmet = clauses.filter((clause) => clause.status === 'missing' || clause.status === 'late').length;
    return { clauses, unmet };
  });
  scored.sort((a, b) => a.unmet - b.unmet);
  return scored[0].clauses;
}

/** Earliest term (order index) in which every prerequisite clause is satisfied and the season fits. */
export function earliestReadyOrder(code: string, terms: PlannerTerm[], index: CatalogIndex, completed: Set<string>, placement: ReturnType<typeof buildPlacement>): number | null {
  const seasons = offeredSeasons(code);
  for (let order = 0; order < terms.length; order++) {
    if (seasons && terms[order].season && !seasons.includes(terms[order].season as string)) continue;
    const clauses = prerequisiteClauses(code, order, index, completed, placement);
    if (clauses.every((clause) => clause.status === 'earned' || clause.status === 'planned' || clause.status === 'unknown')) return order;
  }
  return null;
}

export interface GraphNode {
  code: string;
  column: number;
  row: number;
  kind: 'earned' | 'planned' | 'next';
  title: string;
  hasIssue: boolean;
}

export interface GraphEdge {
  from: string;
  to: string;
  late: boolean;
}

/**
 * Dependency graph laid out by term. Column 0 holds earned courses that feed the plan,
 * one column per term follows, and a last column suggests requirement courses that the
 * plan unlocks but that are not yet placed.
 */
export function buildDependencyGraph(
  terms: PlannerTerm[],
  index: CatalogIndex,
  completed: Set<string>,
  placement: ReturnType<typeof buildPlacement>,
  programs: PlannerProgram[],
  issueCodes: Set<string>,
  nameOf: (code: string) => string,
): { nodes: GraphNode[]; edges: GraphEdge[]; columns: string[] } {
  const columns = ['Earned', ...terms.map((term) => term.name), 'Unlocks next'];
  const nodes = new Map<string, GraphNode>();
  const edges: GraphEdge[] = [];
  const planned = Array.from(placement.entries());

  planned.forEach(([code, where]) => {
    nodes.set(code, { code, column: where.order + 1, row: 0, kind: 'planned', title: nameOf(code), hasIssue: issueCodes.has(code) });
  });
  planned.forEach(([code, where]) => {
    prerequisiteClauses(code, where.order, index, completed, placement).forEach((clause) => {
      if (!clause.satisfiedBy) return;
      const from = clause.satisfiedBy;
      if (clause.status === 'earned' && !nodes.has(from)) {
        nodes.set(from, { code: from, column: 0, row: 0, kind: 'earned', title: nameOf(from), hasIssue: false });
      }
      if (nodes.has(from) || placement.has(from)) edges.push({ from, to: code, late: clause.status === 'late' });
    });
  });

  // Requirement courses that this plan makes reachable but has not placed yet.
  const requirementPool = new Set(programs.flatMap((program) => program.requirements.filter((req) => req.openCount > 0).flatMap((req) => req.pool)));
  const sources = [...placement.keys(), ...Array.from(nodes.values()).filter((n) => n.kind === 'earned').map((n) => n.code)];
  const nextCandidates = new Map<string, string[]>();
  sources.forEach((code) => {
    (index.dependents.get(code) || []).forEach((dependent) => {
      if (placement.has(dependent) || completed.has(dependent) || !requirementPool.has(dependent)) return;
      const list = nextCandidates.get(dependent) || [];
      list.push(code);
      nextCandidates.set(dependent, list);
    });
  });
  const lastOrder = terms.length;
  Array.from(nextCandidates.entries())
    .filter(([code]) => prerequisiteClauses(code, lastOrder, index, completed, placement).every((c) => c.status !== 'missing'))
    .slice(0, 8)
    .forEach(([code, from]) => {
      nodes.set(code, { code, column: columns.length - 1, row: 0, kind: 'next', title: nameOf(code), hasIssue: false });
      from.forEach((source) => { if (nodes.has(source)) edges.push({ from: source, to: code, late: false }); });
    });

  // Order rows within each column by the average row of their predecessors (one sweep).
  const byColumn = new Map<number, GraphNode[]>();
  nodes.forEach((node) => { const list = byColumn.get(node.column) || []; list.push(node); byColumn.set(node.column, list); });
  const sortedColumns = Array.from(byColumn.keys()).sort((a, b) => a - b);
  sortedColumns.forEach((column) => {
    const list = byColumn.get(column) as GraphNode[];
    const score = (node: GraphNode) => {
      const preds = edges.filter((edge) => edge.to === node.code).map((edge) => nodes.get(edge.from)?.row ?? 0);
      return preds.length ? preds.reduce((a, b) => a + b, 0) / preds.length : Number.MAX_SAFE_INTEGER / 2;
    };
    list.sort((a, b) => score(a) - score(b) || a.code.localeCompare(b.code));
    list.forEach((node, row) => { node.row = row; });
  });

  return { nodes: Array.from(nodes.values()), edges, columns };
}
